import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { getAuth, signOut } from 'firebase/auth';
import { getDatabase, ref, get } from 'firebase/database';
import {
  TERMS_URL, PRIVACY_URL, REPORT_REASONS,
  recordTermsAcceptance, blockUser, unblockUser, sendReport, firstName, useBlockedUsers,
} from '../safety';
import './Safety.css';

/* ── Toast ────────────────────────────────────────────────────── */

/**
 * Short confirmation at the bottom of the screen. Plain DOM, so it survives
 * the card it came from disappearing (e.g. after a block).
 */
export function showToast(message) {
  const el = document.createElement('div');
  el.className = 'safety-toast';
  el.setAttribute('role', 'status');
  el.textContent = message;
  document.body.appendChild(el);
  setTimeout(() => el.classList.add('leaving'), 2400);
  setTimeout(() => el.remove(), 2800);
}

/* ── Terms agreement (existing users, shown once) ─────────────── */

const TERMS_POINTS = [
  'Falcon Ride connects people sharing rides. Riders and drivers arrange rides directly and at their own risk.',
  'Zero tolerance for harassment, threats, discrimination, unsafe driving, or spam.',
  'Report or block anyone from the ••• menu. Reports are reviewed within 24 hours, and accounts that break the rules are removed.',
  "Listed amounts are voluntary contributions. Falcon Ride doesn't handle payments.",
];

export function TermsAgreement() {
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');

  const agree = async () => {
    const uid = getAuth().currentUser?.uid;
    if (!uid) return;
    setSaving(true); setError('');
    try {
      await recordTermsAcceptance(uid); // the listener in useNeedsTerms hides this screen
    } catch (e) {
      setError(`Couldn't save. ${e.message}`);
      setSaving(false);
    }
  };

  return (
    <div className="terms-screen" role="dialog" aria-modal="true" aria-labelledby="terms-title">
      <div className="terms-card">
        <div className="terms-icon" aria-hidden="true">
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 11 14 15 10"/>
          </svg>
        </div>
        <h1 id="terms-title">We've added Terms of Use</h1>
        <p className="terms-sub">Please read and agree to keep using Falcon Ride.</p>
        <ul className="terms-points">
          {TERMS_POINTS.map(p => <li key={p}>{p}</li>)}
        </ul>
        <a className="terms-link" href={TERMS_URL} target="_blank" rel="noopener noreferrer">Read the full Terms of Use</a>
        {error && <p className="terms-error">{error}</p>}
        <button className="terms-agree" onClick={agree} disabled={saving}>
          {saving ? 'Saving…' : 'I agree'}
        </button>
        <button className="terms-logout" onClick={() => signOut(getAuth())}>Log out</button>
      </div>
    </div>
  );
}

/** "I agree to the Terms of Use and Privacy Policy" for the sign-up form. */
export function TermsCheckbox({ checked, onChange }) {
  return (
    <label className="terms-check">
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} />
      <span>
        I agree to the{' '}
        <a href={TERMS_URL} target="_blank" rel="noopener noreferrer">Terms of Use</a>{' '}
        and{' '}
        <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
        No tolerance for abusive behavior or objectionable content.
      </span>
    </label>
  );
}

/* ── ••• menu: Report / Block ─────────────────────────────────── */

/**
 * target = { userID, name, contextType, contextId }.
 * onBlocked runs after a block is saved (e.g. to close a card).
 */
export function SafetyMenu({ target, onBlocked }) {
  const [anchor,     setAnchor]     = useState(null); // button rect while open
  const [reporting,  setReporting]  = useState(false);
  const btnRef  = useRef(null);
  const listRef = useRef(null);
  const first = firstName(target.name);
  const open = anchor !== null;

  // The list is portaled to <body> so cards with overflow: hidden can't clip it.
  useEffect(() => {
    if (!open) return;
    const hide  = () => setAnchor(null);
    const close = e => {
      if (!btnRef.current?.contains(e.target) && !listRef.current?.contains(e.target)) hide();
    };
    const esc = e => { if (e.key === 'Escape') hide(); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    window.addEventListener('scroll', hide, true);
    window.addEventListener('resize', hide);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', esc);
      window.removeEventListener('scroll', hide, true);
      window.removeEventListener('resize', hide);
    };
  }, [open]);

  const toggle = () => setAnchor(a => (a ? null : btnRef.current.getBoundingClientRect()));
  const closeMenu = () => setAnchor(null);

  const block = async () => {
    closeMenu();
    const ok = window.confirm(
      `Block ${first}?\n\nYou won't see their rides or requests, and you won't get notifications about them. ` +
      "They won't be told. You can unblock them in Settings."
    );
    if (!ok) return;
    try {
      await blockUser(target.userID);
      showToast(`${first} blocked`);
      onBlocked?.();
    } catch (e) {
      alert(`Couldn't block ${first}. ${e.message}`);
    }
  };

  return (
    <div className="safety-menu">
      <button
        ref={btnRef}
        type="button"
        className="safety-menu-btn"
        aria-label={`More options for ${first}`}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={toggle}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <circle cx="5" cy="12" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="19" cy="12" r="2"/>
        </svg>
      </button>
      {open && ReactDOM.createPortal(
        <div className="safety-menu-list" role="menu" ref={listRef} style={{
          right: window.innerWidth - anchor.right,
          // Open upward when there's no room below (leaving space for the phone tab bar).
          ...(window.innerHeight - anchor.bottom < 180
            ? { bottom: window.innerHeight - anchor.top + 4 }
            : { top: anchor.bottom + 4 }),
        }}>
          <button type="button" role="menuitem" onClick={() => { closeMenu(); setReporting(true); }}>
            Report {first}
          </button>
          <button type="button" role="menuitem" className="danger" onClick={block}>
            Block {first}
          </button>
        </div>,
        document.body
      )}
      {reporting && <ReportModal target={target} onClose={() => setReporting(false)} onBlocked={onBlocked} />}
    </div>
  );
}

/* ── Report form ──────────────────────────────────────────────── */

export function ReportModal({ target, onClose, onBlocked }) {
  const [reason,    setReason]    = useState('');
  const [note,      setNote]      = useState('');
  const [alsoBlock, setAlsoBlock] = useState(false);
  const [sending,   setSending]   = useState(false);
  const [error,     setError]     = useState('');
  const first = firstName(target.name);

  useEffect(() => {
    const esc = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [onClose]);

  const send = async () => {
    if (!reason) return;
    setSending(true); setError('');
    try {
      await sendReport({
        reported: target.userID, reason, note,
        contextType: target.contextType, contextId: target.contextId,
      });
      if (alsoBlock) await blockUser(target.userID);
      showToast(alsoBlock ? `Report sent and ${first} blocked` : 'Report sent. Thanks for keeping Falcon Ride safe');
      onClose();
      if (alsoBlock) onBlocked?.();
    } catch (e) {
      setError(`Couldn't send the report. ${e.message}`);
      setSending(false);
    }
  };

  return ReactDOM.createPortal(
    <div className="safety-overlay" onClick={onClose}>
      <div className="safety-dialog" role="dialog" aria-modal="true" aria-labelledby="report-title"
        onClick={e => e.stopPropagation()}>
        <h2 id="report-title">Report {first}</h2>
        <fieldset className="report-reasons">
          <legend>What happened?</legend>
          {REPORT_REASONS.map(r => (
            <label key={r.value} className={`report-reason${reason === r.value ? ' selected' : ''}`}>
              <input type="radio" name="report-reason" value={r.value}
                checked={reason === r.value} onChange={() => setReason(r.value)} />
              {r.label}
            </label>
          ))}
        </fieldset>
        <textarea className="report-note" rows={3} maxLength={500}
          placeholder="Add details (optional)" value={note}
          onChange={e => setNote(e.target.value)} />
        <p className="report-hint">Reports are private. {first} won't see who reported them.</p>
        <label className="report-block">
          <input type="checkbox" checked={alsoBlock} onChange={e => setAlsoBlock(e.target.checked)} />
          Also block {first}
        </label>
        {error && <p className="report-error">{error}</p>}
        <div className="safety-actions">
          <button type="button" className="safety-secondary" onClick={onClose}>Cancel</button>
          <button type="button" className="safety-primary" onClick={send} disabled={!reason || sending}>
            {sending ? 'Sending…' : 'Send report'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ── Settings → Blocked users ─────────────────────────────────── */

export function BlockedUsersList() {
  const blocked = useBlockedUsers();
  const [names, setNames] = useState({});

  useEffect(() => {
    const db = getDatabase();
    blocked.forEach(uid => {
      if (names[uid] !== undefined) return;
      get(ref(db, `users/${uid}/name`))
        .then(s => setNames(n => ({ ...n, [uid]: s.val() || 'Falcon Ride user' })))
        .catch(() => setNames(n => ({ ...n, [uid]: 'Falcon Ride user' })));
    });
  }, [blocked, names]);

  if (blocked.size === 0) return <p className="blocked-empty">You haven't blocked anyone.</p>;

  return (
    <ul className="blocked-list">
      {[...blocked].sort().map(uid => (
        <li key={uid}>
          <span>{names[uid] ?? '…'}</span>
          <button type="button" onClick={() => unblockUser(uid).catch(e => alert(e.message))}>Unblock</button>
        </li>
      ))}
    </ul>
  );
}
